import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { getLocalDateKey } from "./dateKeys";

/**
 * Clears all habit slot checkmarks when the local calendar day changes.
 * Uses taskReset/{userId}.lastResetDate so only one reset runs per day.
 * @returns {Promise<boolean>} true if tasks were reset
 */
export async function resetDailyTasksIfNeeded(userId) {
  if (!userId) return false;

  const today = getLocalDateKey();
  const resetRef = doc(firestore, "taskReset", userId);
  const resetSnap = await getDoc(resetRef);
  const lastReset = resetSnap.exists() ? resetSnap.data().lastResetDate : null;
  if (lastReset && lastReset >= today) return false;

  const tasksSnap = await getDocs(
    query(collection(firestore, "tasks"), where("userId", "==", userId))
  );
  if (tasksSnap.empty) {
    const batch = writeBatch(firestore);
    batch.set(resetRef, { lastResetDate: today }, { merge: true });
    await batch.commit();
    return false;
  }

  const batch = writeBatch(firestore);
  tasksSnap.docs.forEach((taskDoc) => {
    const data = taskDoc.data();
    const completed =
      Array.isArray(data.completed) && data.completed.length > 0
        ? data.completed.map(() => false)
        : [false];
    batch.update(taskDoc.ref, { completed });
  });
  batch.set(resetRef, { lastResetDate: today }, { merge: true });
  await batch.commit();
  return true;
}

/** Milliseconds until the next local midnight (+1s buffer). */
export function msUntilNextLocalMidnight(from = new Date()) {
  const next = new Date(from);
  next.setDate(next.getDate() + 1);
  next.setHours(0, 0, 0, 0);
  return Math.max(1000, next.getTime() - from.getTime() + 1000);
}
